const SELECTED_ITEMS_BASE_KEY = "selectedItemsStore";
const USER_SCOPE_KEY = "appUserStorageKey";
const BASIC_AUTH_USER_KEY = "basicAuthUser";

const parseStoreObject = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const getCurrentUserScope = () => {
  if (typeof window === "undefined") return "anonymous";

  const windowScope = window.__appUserStorageKey;
  if (windowScope && typeof windowScope === "string") return windowScope;

  try {
    const storedScope = localStorage.getItem(USER_SCOPE_KEY);
    if (storedScope) return storedScope;
  } catch {
    // Ignore storage access errors.
  }

  try {
    const basicAuthUser = localStorage.getItem(BASIC_AUTH_USER_KEY);
    if (basicAuthUser) return `basic:${basicAuthUser}`;
  } catch {
    // Ignore storage access errors.
  }

  return "anonymous";
};

export const getSelectedItemsStorageKey = () =>
  `${SELECTED_ITEMS_BASE_KEY}::${getCurrentUserScope()}`;

const loadSelectedItemsObject = () => {
  if (typeof window === "undefined") return {};

  const scopedKey = getSelectedItemsStorageKey();
  const scopedStore = parseStoreObject(localStorage.getItem(scopedKey));
  if (Object.keys(scopedStore).length > 0) return scopedStore;

  // One-time migration from legacy global key into current user scope.
  const legacyRaw = localStorage.getItem(SELECTED_ITEMS_BASE_KEY);
  const legacyStore = parseStoreObject(legacyRaw);
  if (Object.keys(legacyStore).length > 0) {
    localStorage.setItem(scopedKey, JSON.stringify(legacyStore));
    localStorage.removeItem(SELECTED_ITEMS_BASE_KEY);
    return legacyStore;
  }

  return {};
};

export const ensureSelectedItemsStoreForCurrentUser = () => {
  if (typeof window === "undefined") return new Map();

  const scope = getCurrentUserScope();
  if (!window.selectedItemsStore || window.__selectedItemsStoreScope !== scope) {
    const storeObject = loadSelectedItemsObject();
    window.selectedItemsStore = new Map();
    Object.entries(storeObject).forEach(([sessionId, ids]) => {
      if (Array.isArray(ids)) {
        window.selectedItemsStore.set(sessionId, ids);
      }
    });
    window.__selectedItemsStoreScope = scope;
  }

  return window.selectedItemsStore;
};

export const persistSelectedItemsStoreForCurrentUser = (storeMap) => {
  if (typeof window === "undefined") return;

  const store = storeMap || ensureSelectedItemsStoreForCurrentUser();
  const storeObj = {};
  store.forEach((value, key) => {
    if (Array.isArray(value)) {
      storeObj[key] = value;
    }
  });
  localStorage.setItem(getSelectedItemsStorageKey(), JSON.stringify(storeObj));
};

export const loadSelectedItemsForCurrentUser = () =>
  parseStoreObject(localStorage.getItem(getSelectedItemsStorageKey()));

export const saveSelectedItemsForCurrentUser = (storeObj) => {
  localStorage.setItem(getSelectedItemsStorageKey(), JSON.stringify(storeObj || {}));
};

export const clearSelectedItemsForCurrentUser = () => {
  localStorage.removeItem(getSelectedItemsStorageKey());
};

export const setAppUserStorageScope = (scope) => {
  if (typeof window === "undefined") return;

  if (scope) {
    window.__appUserStorageKey = scope;
    localStorage.setItem(USER_SCOPE_KEY, scope);
    return;
  }

  delete window.__appUserStorageKey;
  localStorage.removeItem(USER_SCOPE_KEY);
};
